
import { Link } from 'react-router-dom';
import { Mail, Phone } from 'lucide-react';
import { useState } from 'react';

export default function Footer() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('loading');

    try {
      const response = await fetch('https://readdy.ai/api/public/form/submit/livepetal-newsletter', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({ email }).toString(),
      });

      if (response.ok) {
        setStatus('success');
        setEmail('');
        setTimeout(() => setStatus('idle'), 3000);
      } else {
        setStatus('error');
        setTimeout(() => setStatus('idle'), 3000);
      }
    } catch (error) {
      setStatus('error');
      setTimeout(() => setStatus('idle'), 3000);
    }
  };

  return (
    <footer className="relative bg-[#0D1B4F] text-white overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#E8621A] rounded-full mix-blend-multiply filter blur-3xl"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto container-padding section-spacing-sm">
        {/* Main footer content */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12 mb-8 lg:mb-12">
          {/* Column 1: Brand */}
          <div className="text-center sm:text-left">
            <Link to="/" className="inline-block mb-4">
              <h3 className="font-['Syne'] text-2xl lg:text-3xl font-bold">
                Live<span className="text-[#E8621A]">petal</span>
              </h3>
            </Link>
            <p className="font-['DM_Sans'] text-sm text-white/80 mb-6 leading-relaxed">
              Powering Digital Transformation — One Solution at a Time
            </p>
            
            {/* Social icons */}
            <div className="flex gap-3 justify-center sm:justify-start">
              <a 
                href="https://x.com/livepetal" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 flex items-center justify-center hover:bg-[#E8621A] hover:border-[#E8621A] hover:scale-110 transition-all duration-300 cursor-pointer"
                aria-label="Twitter"
              >
                <i className="ri-twitter-x-line text-lg"></i>
              </a>
              <a 
                href="https://linkedin.com/company/livepetal" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 flex items-center justify-center hover:bg-[#E8621A] hover:border-[#E8621A] hover:scale-110 transition-all duration-300 cursor-pointer"
                aria-label="LinkedIn"
              >
                <i className="ri-linkedin-line text-lg"></i>
              </a>
              <a 
                href="https://www.facebook.com/livepetals" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 flex items-center justify-center hover:bg-[#E8621A] hover:border-[#E8621A] hover:scale-110 transition-all duration-300 cursor-pointer"
                aria-label="Facebook"
              >
                <i className="ri-facebook-line text-lg"></i>
              </a>
              <a 
                href="https://instagram.com/livepetal" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 flex items-center justify-center hover:bg-[#E8621A] hover:border-[#E8621A] hover:scale-110 transition-all duration-300 cursor-pointer"
                aria-label="Instagram"
              >
                <i className="ri-instagram-line text-lg"></i>
              </a>
              <a 
                href="https://wa.me/2348032716283" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 flex items-center justify-center hover:bg-[#E8621A] hover:border-[#E8621A] hover:scale-110 transition-all duration-300 cursor-pointer"
                aria-label="WhatsApp"
              >
                <i className="ri-whatsapp-line text-lg"></i>
              </a>
            </div>
          </div>

          {/* Column 2: Products Group 1 */}
          <div className="text-center sm:text-left">
            <h4 className="font-['Syne'] text-base lg:text-lg font-bold mb-4">Products</h4>
            <ul className="space-y-3 font-['DM_Sans'] text-sm">
              <li>
                <Link to="/products/salespro" className="text-white/80 hover:text-[#E8621A] transition-colors duration-300">
                  SalesPro
                </Link>
              </li>
              <li>
                <Link to="/products/wordpro" className="text-white/80 hover:text-[#E8621A] transition-colors duration-300">
                  WordPro
                </Link>
              </li>
              <li>
                <Link to="/products/braveiq" className="text-white/80 hover:text-[#E8621A] transition-colors duration-300">
                  BraveIQ
                </Link>
              </li>
              <li>
                <Link to="/products/schoolpetal" className="text-white/80 hover:text-[#E8621A] transition-colors duration-300">
                  SchoolPetal
                </Link>
              </li>
            </ul>
          </div>

          {/* Column 3: Products Group 2 */}
          <div className="text-center sm:text-left">
            <h4 className="font-['Syne'] text-base lg:text-lg font-bold mb-4">More Products</h4>
            <ul className="space-y-3 font-['DM_Sans'] text-sm">
              <li>
                <Link to="/products/timetrackpro" className="text-white/80 hover:text-[#E8621A] transition-colors duration-300">
                  TimeTrack Pro
                </Link>
              </li>
              <li>
                <a 
                  href="https://cartmor.com" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-white/80 hover:text-[#E8621A] transition-colors duration-300"
                >
                  Cartmor
                </a>
              </li>
              <li>
                <Link to="/products/bmserp" className="text-white/80 hover:text-[#E8621A] transition-colors duration-300">
                  BMS ERP
                </Link>
              </li>
            </ul>
          </div>

          {/* Column 4: Get in Touch */}
          <div className="text-center sm:text-left">
            <h4 className="font-['Syne'] text-base lg:text-lg font-bold mb-4">Get in Touch</h4>
            
            {/* Contact info */}
            <div className="space-y-3 mb-6">
              <a 
                href="mailto:info@livepetal.com"
                className="flex items-center gap-2 text-sm text-white/80 hover:text-[#E8621A] transition-colors duration-300 justify-center sm:justify-start"
              >
                <Mail className="w-4 h-4 flex-shrink-0" />
                <span className="font-['DM_Sans']">info@livepetal.com</span>
              </a>
              <a 
                href="https://wa.me/2348032716283"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-sm text-white/80 hover:text-[#E8621A] transition-colors duration-300 justify-center sm:justify-start"
              >
                <Phone className="w-4 h-4 flex-shrink-0" />
                <span className="font-['DM_Sans']">+234 803 271 6283</span>
              </a>
              <div className="flex items-start gap-2 text-sm text-white/80 justify-center sm:justify-start">
                <i className="ri-map-pin-line w-4 h-4 flex-shrink-0 mt-0.5"></i>
                <span className="font-['DM_Sans']">24, State Industrial Layout, 2nd Floor, Bovas Petrol Station, Beside FUTA Junction, Ilesha Road, Akure</span>
              </div>
            </div>

            {/* Newsletter signup */}
            <div>
              <p className="font-['DM_Sans'] text-sm text-white/80 mb-3">Subscribe to our newsletter</p>
              <form onSubmit={handleNewsletterSubmit} className="flex flex-col gap-2" data-readdy-form id="livepetal-newsletter">
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Your email"
                  required
                  className="px-4 py-2.5 rounded-lg bg-white/10 backdrop-blur-sm border border-white/20 text-white placeholder-white/50 focus:outline-none focus:border-[#E8621A] transition-colors duration-300 text-sm"
                />
                <button
                  type="submit"
                  disabled={status === 'loading'}
                  className="px-4 py-2.5 bg-[#E8621A] text-white rounded-lg font-['DM_Sans'] font-bold hover:bg-[#FF8C42] hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed text-sm whitespace-nowrap"
                >
                  {status === 'loading' ? 'Subscribing...' : status === 'success' ? 'Subscribed!' : status === 'error' ? 'Try Again' : 'Subscribe'}
                </button>
              </form>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="pt-6 lg:pt-8 border-t border-white/10">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 font-['DM_Sans'] text-xs lg:text-sm text-white/60">
            <p className="text-center md:text-left">© 2026 Livepetal Systems Limited. All Rights Reserved.</p>
            <div className="flex gap-4 lg:gap-6">
              <Link to="/privacy" className="hover:text-[#E8621A] transition-colors duration-300">
                Privacy Policy
              </Link>
              <Link to="/terms" className="hover:text-[#E8621A] transition-colors duration-300">
                Terms
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}